// Product brief for the generic two-pool hybrid life insurance strategy. See
// cmTwo.ts for the format intent.

export const genericHybridBrief = `**Generic Hybrid Life Insurance** — minimal two-pool DB + COB model.

Ground truth by product:
- **Allstate Whole Life with LTC** (\`allstateHybridPolicy\`) and **Trustmark
  Life + Care** (\`agisGroupPolicy\`): ⚠️ **genuinely UNVERIFIED** — no carrier
  illustration in this repo. Every mechanic mirrors the legacy generic hybrid
  calc (\`calcFundingSourceDerivatives.ts\` hybrid branch). Treat their figures
  as the model's output, not carrier-confirmed values, and do NOT invent
  numbers from an illustration that does not exist for them.
- Both carrier types carry \`hasUnverifiedModeling: true\` and lack any in-repo
  ground truth.
- (Securian SecureCare III/IV split off to their own \`secure-care\` strategy —
  duration-derived pools, always-indemnity, proportional DB.)

Pool model — two pools, drawn in order:
- **Death-benefit / base pool** = \`policyMaximumBenefitAmount\` (the accelerable
  face), drawn FIRST.
- **Continuation of Benefits (COB)** = \`policyContinuationBenefitAmount\`, the
  \`eob\` column, drawn only after the base pool is exhausted.
- Each pool inflates independently at the policy anniversary on its own rate
  (base on \`policyInflationProtection\`; COB on
  \`policyInflationProtectionOnCOBAmount ?? policyInflationProtection\`), gated on
  the pool remaining open and within \`policyInflationProtectionYears\`. Simple or
  compound (\`isSimpleInflationProtection\`).

Death benefit — static (BY DESIGN, P2-16/17):
- Reduced **dollar-for-dollar by base-pool drawdown only** (COB drawdown does
  NOT accelerate the face). Floored at \`policyMinimumGuaranteedBenefitAmount\`.
- Held **flat at the at-issue face for every type** (\`dbRate\` pinned at 0),
  including Trustmark (\`agisGroupPolicy\`). Even when the policy carries inflation
  protection (\`policyInflationProtection\` /
  \`policyInflationProtectionOnFaceAmount\`), that rider grows the LTC pools and
  monthly cap — not the life face — mirroring CM II and the other hybrid
  strategies. This deliberately diverges from legacy, which inflated the face
  via \`policyInflationProtectionOnFaceAmount\`.

Payout model — reimbursement (default) or cash indemnity:
- **Reimbursement:** \`min(eligibleCost, proRatedCap)\` per pool, base first then
  COB, each at its own inflated daily cap.
- **Indemnity** (\`isIndemnityPolicyPayment\`): the full pro-rated monthly cap as
  cash regardless of cost, drawn base-then-COB. Excess over eligible cost accrues
  as \`indemnity_surplus\` and is drained on a later shortfall month.

Elimination period:
- Two-tier EP (home vs. facility), served via unpaid waiting days. **No
  retroactive WP lump** — EP days are fully out-of-pocket (\`in-wp\` rows).

Things to eyeball:
- Base depletes before any COB drawdown — \`eob\` stays flat until \`base_pool\` hits 0.
- Death benefit falls in step with base drawdown only, and is otherwise flat at
  the at-issue face for every type (Allstate / Trustmark alike).
- Indemnity surplus accrues on low-cost months and drains on high-cost months.
- No EP reimbursement lump anywhere.
`;
